import type { ReactNode } from "react";

import type { EventCardTarget } from "components/EventSummaryCard";
import { cn } from "utils";

import { eventPhase } from "./event-dates";
import {
  DraftChip,
  EndedChip,
  EventCardShell,
  EventMetaLine,
  eventInitial,
  type EventCardEvent,
} from "./EventTileParts";

/**
 * Every other event, as a row: a square thumbnail (the image, or a blue
 * tile with the event's initial), then title, dates, place and the markers.
 *
 * A row is at least 6rem tall, so it is a comfortable target for a thumb, and
 * the thumbnail has a fixed size so the text does not shift as an image loads.
 * The thumbnail is decoration: the title next to it names the event.
 */
export function EventRowCard({
  event,
  target,
  today,
  badges,
  note,
  unpublished = false,
  ended: endedOverride,
  testId = "event-card",
}: {
  event: EventCardEvent;
  target?: EventCardTarget | null;
  /** Today's `yyyy-mm-dd`, which decides whether the event has ended. */
  today: string;
  /** Markers such as Registered and Admin. */
  badges?: ReactNode;
  /** A short line under the details, for why the card leads nowhere. */
  note?: ReactNode;
  unpublished?: boolean;
  /**
   * Whether the event is over, when the caller has judged it on the event's own
   * zone (event-past.ts). Without it the card counts on the reader's day.
   */
  ended?: boolean;
  testId?: string;
}) {
  const ended =
    endedOverride ??
    eventPhase(event.startDate, event.endDate, today).kind === "ended";

  return (
    <EventCardShell
      target={target}
      testId={testId}
      variant="row"
      unpublished={unpublished}
      className="group flex min-h-24 gap-3.5 rounded-[1.375rem] border border-border bg-card p-3 text-card-foreground transition-shadow hover:shadow-card-hover"
    >
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        <h3 className="font-display text-[1.375rem] font-normal leading-[1.12] text-card-foreground wrap-anywhere">
          {event.title}
        </h3>
        <EventMetaLine
          event={event}
          today={today}
          className="text-sm text-muted-foreground"
        />
        {(ended || unpublished || badges) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {ended && <EndedChip />}
            {unpublished && <DraftChip />}
            {badges}
          </div>
        )}
        {note && (
          <p
            className={cn(
              "mt-1.5 text-xs",
              unpublished
                ? "font-medium text-foreground"
                : "text-muted-foreground",
            )}
            data-testid={`${testId}-note`}
          >
            {note}
          </p>
        )}
      </div>

      {/* After the text in the DOM, so the title is read first and not the
          initial; first on screen through `order`. */}
      <div
        data-testid="event-thumb"
        aria-hidden="true"
        className={cn(
          "order-first flex size-18 shrink-0 items-center justify-center overflow-hidden rounded-2xl",
          !event.image && "bg-hb-blue-fill text-white",
        )}
      >
        {event.image ? (
          <img
            src={event.image}
            alt=""
            className={cn(
              "h-full w-full object-cover",
              unpublished && "opacity-60 grayscale",
            )}
          />
        ) : (
          <span className="font-display text-3xl">
            {eventInitial(event.title)}
          </span>
        )}
      </div>
    </EventCardShell>
  );
}
